import { v4 as uuidv4 } from 'uuid'
import { normalizeIconPath } from 'services/util'
import { FAILURE_MESSAGE_CATEGORIES, FAILURE_MESSAGE_DEFAULTS } from './constants'
import { CustomCheckoutFormValues, FailureMessageCategory } from './types'

export const getNestedErrorMessage = (
  errors: Record<string, any>,
  path: string
): string | undefined => {
  const parts = path.split('.')
  let current: any = errors

  for (const part of parts) {
    if (!current?.[part]) return undefined
    current = current[part]
  }

  return current?.message
}

export const generatePaymentMethodSlug = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const withPaymentMethodSlugs = <
  T extends { name: string; slug: string }
>(
  methods: T[]
): T[] =>
  methods.map((method) => ({
    ...method,
    slug: generatePaymentMethodSlug(method.name),
  }))

const toApiIcon = (icon: string) => normalizeIconPath(icon)

const getResponseBody = (response: any) => response?.data?.data ?? response?.data

export const extractCustomCheckoutRecord = (response: any) => {
  const body = getResponseBody(response)
  if (!body || typeof body !== 'object') return null

  if (Array.isArray(body.results)) {
    return body.results[0] ?? null
  }

  if (body.id != null && body.bottomSheet) {
    return body
  }

  return null
}

export const extractCustomCheckoutId = (
  response: any
): string | number | null => {
  const record = extractCustomCheckoutRecord(response)
  if (record?.id == null) return null
  return record.id
}

const mapStatusScreen = (screen: any) => ({
  image: normalizeIconPath(screen?.image ?? ''),
  title: screen?.title ?? '',
  description: screen?.description ?? '',
  ctaText: screen?.ctaText ?? '',
})

const mapFailureMessages = (apiMessages: any[] = []) => {
  const byCategory = new Map<string, any>(
    apiMessages.map((message) => [message?.category, message])
  )

  return FAILURE_MESSAGE_CATEGORIES.map((category) => {
    const existing = byCategory.get(category)

    if (existing) {
      return {
        id: existing?.id != null ? String(existing.id) : uuidv4(),
        category: category as FailureMessageCategory,
        image: normalizeIconPath(existing?.image ?? ''),
        title: existing?.title ?? '',
        description: existing?.description ?? '',
        ctaText: existing?.ctaText ?? '',
        isNew: false,
      }
    }

    return {
      id: uuidv4(),
      category,
      ...FAILURE_MESSAGE_DEFAULTS[category],
      isNew: true,
    }
  })
}

const getPaymentMethodsFromApi = (apiData: any): any[] =>
  apiData?.bottomSheet?.paymentMethods ??
  apiData?.bottomSheetConfig?.paymentMethods ??
  apiData?.commonConfig?.paymentMethods ??
  apiData?.paymentMethods ??
  []

const getFailureMessagesFromApi = (apiData: any): any[] =>
  apiData?.failureMessages ??
  apiData?.failureMessagesConfig ??
  apiData?.commonConfig?.failureMessages ??
  apiData?.paymentFailureCatalog ??
  apiData?.paymentFailureMessages ??
  []

export const mapApiToFormValues = (
  apiData: any
): CustomCheckoutFormValues => ({
  bottomSheet: {
    heading: apiData?.bottomSheet?.heading ?? apiData?.bottomSheetConfig?.heading ?? '',
    informationSections: (
      apiData?.bottomSheet?.informationSections ??
      apiData?.bottomSheetConfig?.informationSections ??
      []
    ).map(
      (section: any) => ({
        id: section?.id != null ? String(section.id) : uuidv4(),
        icon: normalizeIconPath(section?.icon ?? ''),
        text: section?.text ?? '',
        isNew: false,
      })
    ),
    paymentMethods: getPaymentMethodsFromApi(apiData).map(
      (method: any) => ({
        id: method?.id != null ? String(method.id) : uuidv4(),
        name: method?.name ?? '',
        icon: normalizeIconPath(method?.icon ?? ''),
        slug: method?.slug ?? generatePaymentMethodSlug(method?.name ?? ''),
        enabled: method?.enabled ?? true,
        isNew: false,
      })
    ),
  },
  qrDisplay: {
    heading:
      apiData?.qrDisplay?.heading ??
      apiData?.scanner?.heading ??
      apiData?.scannerConfig?.heading ??
      '',
    description:
      apiData?.qrDisplay?.description ??
      apiData?.scanner?.description ??
      apiData?.scannerConfig?.description ??
      '',
    instructionText: apiData?.qrDisplay?.instructionText ?? '',
    shareMessage: apiData?.qrDisplay?.shareMessage ?? '',
  },
  loadingPage: {
    heading:
      apiData?.loadingPage?.heading ?? apiData?.scanner?.loadingPageHeading ?? '',
    description:
      apiData?.loadingPage?.description ??
      apiData?.scanner?.loadingPageDescription ??
      '',
    timeLimitSeconds: Number(apiData?.loadingPage?.timeLimitSeconds ?? 60),
  },
  confirmingLoader: {
    heading: apiData?.confirmingLoader?.heading ?? apiData?.confirmingLoaderConfig?.heading ?? '',
    description:
      apiData?.confirmingLoader?.description ??
      apiData?.confirmingLoaderConfig?.description ??
      '',
  },
  paymentExpired: mapStatusScreen(apiData?.paymentExpired ?? apiData?.paymentExpiredConfig),
  refundInitiated: mapStatusScreen(apiData?.refundInitiated ?? apiData?.refundInitiatedConfig),
  failureMessages: mapFailureMessages(getFailureMessagesFromApi(apiData)),
  cancelPopup: {
    heading: apiData?.cancelPopup?.heading ?? apiData?.cancelPopupConfig?.heading ?? '',
    description:
      apiData?.cancelPopup?.description ??
      apiData?.cancelPopupConfig?.description ??
      '',
    ctaText1:
      apiData?.cancelPopup?.ctaText1 ??
      apiData?.cancelPopupConfig?.ctaText1 ??
      apiData?.cancelPopupConfig?.primaryCtaText ??
      '',
    ctaText2:
      apiData?.cancelPopup?.ctaText2 ??
      apiData?.cancelPopupConfig?.ctaText2 ??
      apiData?.cancelPopupConfig?.secondaryCtaText ??
      '',
  },
})

export const mapFormToApiPayload = (
  values: CustomCheckoutFormValues,
  isUpdate = false,
  existingRecord: any = {}
) => {
  const paymentMethods = withPaymentMethodSlugs(values.bottomSheet.paymentMethods)
  const mappedPaymentMethods = paymentMethods.map((method) => {
    const payload: Record<string, unknown> = {
      name: method.name,
      icon: toApiIcon(method.icon),
      slug: method.slug,
      enabled: method.enabled,
    }

    if (isUpdate && !method.isNew) {
      payload.id = method.id
    }

    return payload
  })
  const mappedFailureMessages = values.failureMessages.map((message) => {
    const payload: Record<string, unknown> = {
      category: message.category,
      image: toApiIcon(message.image),
      title: message.title,
      description: message.description,
      ctaText: message.ctaText,
    }

    if (isUpdate && !message.isNew) {
      payload.id = message.id
    }

    return payload
  })

  return {
    ...(existingRecord?.planId ? { planId: existingRecord.planId } : {}),
    commonConfig: {
      ...(existingRecord?.commonConfig ?? {}),
      failureMessages: mappedFailureMessages,
    },
    bottomSheetConfig: {
      ...(existingRecord?.bottomSheetConfig ?? {}),
      paymentMethods: mappedPaymentMethods,
    },
    bottomSheet: {
      heading: values.bottomSheet.heading,
      informationSections: values.bottomSheet.informationSections.map(
        (section) => {
          const payload: Record<string, unknown> = {
            icon: toApiIcon(section.icon),
            text: section.text,
          }

          if (isUpdate && !section.isNew) {
            payload.id = section.id
          }

          return payload
        }
      ),
      paymentMethods: mappedPaymentMethods,
    },
    qrDisplay: values.qrDisplay,
    loadingPage: {
      heading: values.loadingPage.heading,
      description: values.loadingPage.description,
      timeLimitSeconds: Number(values.loadingPage.timeLimitSeconds),
    },
    confirmingLoader: values.confirmingLoader,
    paymentExpired: {
      image: toApiIcon(values.paymentExpired.image),
      title: values.paymentExpired.title,
      description: values.paymentExpired.description,
      ctaText: values.paymentExpired.ctaText,
    },
    refundInitiated: {
      image: toApiIcon(values.refundInitiated.image),
      title: values.refundInitiated.title,
      description: values.refundInitiated.description,
      ctaText: values.refundInitiated.ctaText,
    },
    failureMessages: mappedFailureMessages,
    cancelPopup: values.cancelPopup,
    status: 1,
  }
}
